import React, { useState, useEffect, useCallback } from "react";
import {
  StyleSheet,
  View,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Linking,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import Constants from "expo-constants";
import { openBrowserAsync } from "expo-web-browser";

import { ThemedText } from "@/components/ThemedText";
import { ThemedPanel } from "@/components/ThemedPanel";
import { ThemedButton } from "@/components/ThemedButton";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";
import { useAuth } from "@components/auth/useAuth";
import { useFirebase } from "@components/firebase/useFirebase";
import { getRules, IMetricEntry, IFirestoreTableSize, IPlatformMetricsSummary, formatBytes } from "common247";
import { useThemeColor } from "@/hooks/useThemeColor";
import { BrandColors, Spacing } from "@/constants/ThemeColours";
import {
  getPlatformMetrics,
  capturePlatformMetrics,
} from "@/services/firebase/platform-metrics-functions";


/**
 * Format numeric count values with thousand separators.
 *
 * @param {number} count - Numeric count.
 * @returns {string} Formatted number string.
 */
function formatCount(count: number): string {
  if (typeof count !== "number") return "0";
  return count.toLocaleString();
}

/**
 * Opens an external web URL using the browser.
 *
 * @param {string} url - Destination URL.
 */
async function openExternalUrl(url: string): Promise<void> {
  try {
    if (Platform.OS === "web") {
      if (typeof window !== "undefined") {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } else {
      await openBrowserAsync(url);
    }
  } catch (err) {
    console.warn("[MetricsAdmin] Error opening URL:", err);
    try {
      await Linking.openURL(url);
    } catch (fallbackErr) {
      console.error("[MetricsAdmin] Linking fallback failed:", fallbackErr);
    }
  }
}

/**
 * Formats a self-describing metric entry based on its declared unit.
 *
 * @param {IMetricEntry} metric - Self-describing metric.
 * @returns {string} Formatted representation.
 */
function formatMetricValue(metric: IMetricEntry): string {
  const { value, unit } = metric;
  const numVal = typeof value === "number" ? value : parseFloat(value as string);

  if (isNaN(numVal)) return String(value);

  switch (unit) {
    case "bytes":
      return formatBytes(numVal);
    case "bytes/day":
      return `${formatBytes(numVal)}/day`;
    case "ms":
      return `${numVal.toFixed(0)} ms`;
    case "percent":
      return `${numVal.toFixed(1)}%`;
    case "count":
    default:
      return formatCount(numVal);
  }
}

/**
 * Category headers and icons configuration for dynamic grouping.
 */
const CATEGORY_CONFIG: Record<string, { title: string; icon: keyof typeof Ionicons.glyphMap; accent: string }> = {
  business: {
    title: "Business & Community KPIs",
    icon: "people-outline",
    accent: "#0284C7", // Sky blue (Purple Ban compliant)
  },
  storage: {
    title: "Storage & Capacity Runway",
    icon: "cloud-outline",
    accent: "#10B981", // Emerald green
  },
  performance: {
    title: "Latencies & Performance SLAs",
    icon: "speedometer-outline",
    accent: BrandColors.sungoldOrange.hex,
  },
  system: {
    title: "System & Infrastructure",
    icon: "server-outline",
    accent: "#64748B", // Slate
  },
};

/**
 * Platform Metrics Admin Screen.
 * Provides administrators with visibility into live and scheduled platform KPIs,
 * capacity growth rate, and Firestore database size per table.
 * 
 * Complies with the Platform Logging & Metrics Extensibility Protocol:
 * Any new metric registered on the backend is automatically discovered and rendered.
 *
 * @returns {React.JSX.Element} The rendered admin metrics screen.
 */
export default function MetricsAdminScreen(): React.JSX.Element {
  const { user } = useAuth(true);
  const services = useFirebase();

  const [metrics, setMetrics] = useState<IPlatformMetricsSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [recalculating, setRecalculating] = useState<boolean>(false);
  const [isCached, setIsCached] = useState<boolean>(false);
  const [lastFetched, setLastFetched] = useState<number | null>(null);

  const textMuted = useThemeColor({}, "textMuted");
  const borderColor = useThemeColor({}, "border");
  const cardBg = useThemeColor({}, "card");
  const tintColor = useThemeColor({}, "tint");

  const rules = getRules();
  const isAdmin = user ? rules.isAdmin.can(user.claims, undefined) : false;

  const projectId =
    services?.app?.options?.projectId ||
    Constants.expoConfig?.extra?.PROJECT_ID ||
    process.env.EXPO_PUBLIC_PROJECT_ID ||
    "memories-dev-247";

  const cloudMonitoringDashboardUrl = `https://console.cloud.google.com/monitoring/dashboards?project=${projectId}`;
  const metricsExplorerUrl = `https://console.cloud.google.com/monitoring/metrics-explorer?project=${projectId}`;

  const fetchMetrics = useCallback(async () => {
    if (!services) return;
    setLoading(true);
    try {
      const response = await getPlatformMetrics(services);
      if (response.status && response.metrics) {
        setMetrics(response.metrics);
        setIsCached(!!response.cached);
        setLastFetched(Date.now());
      } else {
        CustomAlert.alert("Error", response.error || "Failed to load platform metrics.");
      }
    } catch (err: any) {
      console.error("[MetricsAdmin] Error fetching platform metrics:", err);
      CustomAlert.alert("Error", err?.message || "Unexpected error while fetching metrics.");
    } finally {
      setLoading(false);
    }
  }, [services]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const handleRecalculateNow = () => {
    if (!services) return;

    CustomAlert.alert(
      "Recalculate Platform Metrics",
      "This will scan all monitored Firestore collections to calculate accurate document counts and storage bytes, and emit structured logs to Google Cloud Logging. Proceed?",
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Recalculate",
          style: "default",
          onPress: async () => {
            setRecalculating(true);
            try {
              const res = await capturePlatformMetrics(services);
              if (res.status && res.metrics) {
                setMetrics(res.metrics);
                setIsCached(false);
                setLastFetched(Date.now());
                CustomAlert.alert("Metrics Snapshot Updated", "Platform metrics and Firestore database sizes have been successfully recalculated.");
              } else {
                CustomAlert.alert("Snapshot Failed", res.error || "Could not complete metrics recalculation.");
              }
            } catch (err: any) {
              console.error("[MetricsAdmin] Recalculation error:", err);
              CustomAlert.alert("Error", err?.message || "Failed to recalculate metrics.");
            } finally {
              setRecalculating(false);
            }
          },
        },
      ]
    );
  };

  if (!user || !isAdmin) {
    return (
      <ThemedScreen title="Platform Metrics">
        <View style={styles.unauthorizedContainer}>
          <ThemedText type="subtitle" style={{ color: "#E74C3C" }}>
            Access Denied
          </ThemedText>
          <ThemedText style={{ color: textMuted, marginTop: 8 }}>
            This observability console is restricted strictly to platform administrators.
          </ThemedText>
        </View>
      </ThemedScreen>
    );
  }

  // Dynamic grouping of metrics by category (auto-discovery)
  const registry = metrics?.metricsRegistry || {};
  const metricsByCategory: Record<string, IMetricEntry[]> = {};

  for (const entry of Object.values(registry)) {
    const cat = entry.category || "business";
    if (!metricsByCategory[cat]) {
      metricsByCategory[cat] = [];
    }
    metricsByCategory[cat].push(entry);
  }

  // Monitored Firestore collections sorted by storage footprint descending
  const firestoreTablesList: IFirestoreTableSize[] = metrics?.firestoreTables
    ? Object.values(metrics.firestoreTables).sort(
        (a, b) => (b.sizeBytes ?? b.totalSizeBytes ?? 0) - (a.sizeBytes ?? a.totalSizeBytes ?? 0)
      )
    : [];

  const totalDbBytes = metrics?.firestoreTotalSizeBytes || 0;
  const totalDbDocs = metrics?.firestoreTotalDocCount || 0;

  return (
    <ThemedScreen
      title="Platform Metrics"
      loading={loading && !recalculating}
      rightIcon={{
        icon: "refresh-outline",
        onPress: fetchMetrics,
        title: "Refresh",
      }}
    >
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Status Bar & Action Banner */}
        <ThemedPanel style={styles.statusBarPanel}>
          <View style={styles.statusInfoRow}>
            <View style={styles.statusBadgeRow}>
              <View
                style={[
                  styles.statusIndicatorDot,
                  { backgroundColor: isCached ? "#F59E0B" : "#10B981" },
                ]}
              />
              <ThemedText type="defaultSemiBold" style={styles.statusBadgeText}>
                {isCached ? "Cached Snapshot" : "Live Snapshot"}
              </ThemedText>
            </View>

            {metrics?.timestamp && (
              <ThemedText style={[styles.statusTimestamp, { color: textMuted }]}>
                Snapshot: {new Date(metrics.timestamp).toLocaleString()}
              </ThemedText>
            )}
          </View>

          <View style={styles.actionButtonsRow}>
            <ThemedButton
              title="Cloud Monitoring"
              onPress={() => openExternalUrl(cloudMonitoringDashboardUrl)}
              icon="open-outline"
              variant="secondary"
              size="small"
              style={styles.actionBtn}
            />
            <ThemedButton
              title="Refresh"
              onPress={fetchMetrics}
              icon="reload-outline"
              variant="secondary"
              size="small"
              disabled={loading || recalculating}
              style={styles.actionBtn}
            />
            <ThemedButton
              title={recalculating ? "Recalculating..." : "Recalculate Now"}
              onPress={handleRecalculateNow}
              icon="flash-outline"
              variant="primary"
              size="small"
              disabled={loading || recalculating}
              style={styles.actionBtn}
            />
          </View>
        </ThemedPanel>

        {/* Cloud Monitoring Infrastructure Callout */}
        <ThemedPanel style={styles.cloudMonitoringPanel}>
          <View style={styles.cloudMonitoringHeader}>
            <View style={styles.cloudMonitoringIcon}>
              <Ionicons name="hardware-chip-outline" size={20} color="#0284C7" />
            </View>
            <View style={styles.cloudMonitoringTextContainer}>
              <View style={styles.cloudMonitoringTitleRow}>
                <ThemedText type="defaultSemiBold" style={styles.cloudMonitoringTitle}>
                  Cloud Monitoring Dashboard (CPU & Infrastructure)
                </ThemedText>
                <View style={styles.projectPill}>
                  <ThemedText style={styles.projectPillText}>
                    {projectId}
                  </ThemedText>
                </View>
              </View>
              <ThemedText style={[styles.cloudMonitoringDesc, { color: textMuted }]}>
                Real-time container CPU utilization (run.googleapis.com/container/cpu/utilizations), memory pressure, Cloud Run instance counts, and network SLAs that are not stored in Firestore are visualised in Google Cloud Monitoring.
              </ThemedText>
            </View>
          </View>

          <View style={styles.cloudMonitoringLinksRow}>
            <TouchableOpacity
              style={[styles.cloudMonitoringLinkBtn, { borderColor }]}
              onPress={() => openExternalUrl(cloudMonitoringDashboardUrl)}
              activeOpacity={0.7}
            >
              <Ionicons name="speedometer-outline" size={16} color={tintColor} />
              <ThemedText style={[styles.cloudMonitoringLinkText, { color: tintColor }]}>
                Open Cloud Monitoring Dashboards
              </ThemedText>
              <Ionicons name="open-outline" size={14} color={tintColor} style={{ marginLeft: "auto" }} />
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.cloudMonitoringLinkBtn, { borderColor }]}
              onPress={() => openExternalUrl(metricsExplorerUrl)}
              activeOpacity={0.7}
            >
              <Ionicons name="analytics-outline" size={16} color={tintColor} />
              <ThemedText style={[styles.cloudMonitoringLinkText, { color: tintColor }]}>
                Metrics Explorer (CPU & Memory)
              </ThemedText>
              <Ionicons name="open-outline" size={14} color={tintColor} style={{ marginLeft: "auto" }} />
            </TouchableOpacity>
          </View>
        </ThemedPanel>

        {recalculating && (
          <ThemedPanel style={styles.loadingBanner}>
            <ActivityIndicator size="small" color={tintColor} />
            <ThemedText style={{ marginLeft: 12, color: tintColor }}>
              Scanning Firestore collections and computing table storage...
            </ThemedText>
          </ThemedPanel>
        )}

        {/* Dynamic Metric Categories (Auto-Discovered) */}
        {Object.entries(metricsByCategory).map(([categoryKey, categoryEntries]) => {
          const config = CATEGORY_CONFIG[categoryKey] || {
            title: `${categoryKey.toUpperCase()} Metrics`,
            icon: "stats-chart-outline" as const,
            accent: "#0284C7",
          };

          return (
            <ThemedPanel key={categoryKey} style={styles.sectionPanel}>
              <View style={styles.sectionHeaderRow}>
                <View
                  style={[
                    styles.sectionIconContainer,
                    { backgroundColor: `${config.accent}15` },
                  ]}
                >
                  <Ionicons name={config.icon} size={18} color={config.accent} />
                </View>
                <ThemedText type="subtitle" style={styles.sectionTitle}>
                  {config.title}
                </ThemedText>
              </View>

              <View style={styles.metricsGrid}>
                {categoryEntries.map((metric) => (
                  <View
                    key={metric.key}
                    style={[
                      styles.metricCard,
                      { backgroundColor: cardBg, borderColor },
                    ]}
                  >
                    <ThemedText style={[styles.metricLabel, { color: textMuted }]} numberOfLines={1}>
                      {metric.label}
                    </ThemedText>
                    <ThemedText type="title" style={styles.metricValue}>
                      {formatMetricValue(metric)}
                    </ThemedText>
                    {metric.description && (
                      <ThemedText
                        style={[styles.metricDescription, { color: textMuted }]}
                        numberOfLines={2}
                      >
                        {metric.description}
                      </ThemedText>
                    )}
                  </View>
                ))}
              </View>
            </ThemedPanel>
          );
        })}

        {/* Firestore Database Per-Table Storage Breakdown */}
        <ThemedPanel style={styles.sectionPanel}>
          <View style={styles.sectionHeaderRow}>
            <View
              style={[
                styles.sectionIconContainer,
                { backgroundColor: "#10B98115" },
              ]}
            >
              <Ionicons name="layers-outline" size={18} color="#10B981" />
            </View>
            <View style={{ flex: 1 }}>
              <ThemedText type="subtitle" style={styles.sectionTitle}>
                Firestore Database Storage Per Collection
              </ThemedText>
              <ThemedText style={{ color: textMuted, fontSize: 12 }}>
                Calculated per Google Firestore storage specification (32-byte overhead, primary keys, fields)
              </ThemedText>
            </View>
          </View>

          {/* Database Total Header */}
          <View style={[styles.dbTotalsBanner, { borderColor }]}>
            <View style={styles.dbTotalItem}>
              <ThemedText style={[styles.dbTotalLabel, { color: textMuted }]}>
                Total Database Footprint
              </ThemedText>
              <ThemedText type="subtitle" style={{ color: "#10B981" }}>
                {formatBytes(totalDbBytes)}
              </ThemedText>
            </View>
            <View style={[styles.dbDivider, { backgroundColor: borderColor }]} />
            <View style={styles.dbTotalItem}>
              <ThemedText style={[styles.dbTotalLabel, { color: textMuted }]}>
                Total Monitored Documents
              </ThemedText>
              <ThemedText type="subtitle">
                {formatCount(totalDbDocs)}
              </ThemedText>
            </View>
            <View style={[styles.dbDivider, { backgroundColor: borderColor }]} />
            <View style={styles.dbTotalItem}>
              <ThemedText style={[styles.dbTotalLabel, { color: textMuted }]}>
                Monitored Collections
              </ThemedText>
              <ThemedText type="subtitle">
                {firestoreTablesList.length}
              </ThemedText>
            </View>
          </View>

          {/* Table List */}
          <View style={styles.tableListContainer}>
            {firestoreTablesList.length === 0 ? (
              <ThemedText style={{ color: textMuted, padding: 16, textAlign: "center" }}>
                No collection storage metrics captured yet. Tap "Recalculate Now" to compute collection sizes.
              </ThemedText>
            ) : (
              firestoreTablesList.map((table) => {
                const tableBytes = table.sizeBytes ?? table.totalSizeBytes ?? 0;
                const percentOfTotal = totalDbBytes > 0 ? (tableBytes / totalDbBytes) * 100 : 0;

                return (
                  <View
                    key={table.collectionName}
                    style={[
                      styles.tableRowCard,
                      { backgroundColor: cardBg, borderColor },
                    ]}
                  >
                    <View style={styles.tableRowTop}>
                      <View style={styles.tableNameBadge}>
                        <Ionicons name="folder-outline" size={14} color="#0284C7" />
                        <ThemedText type="defaultSemiBold" style={styles.tableNameText}>
                          {table.collectionName}
                        </ThemedText>
                      </View>

                      <View style={styles.tableSizeMetrics}>
                        <ThemedText type="defaultSemiBold" style={styles.tableSizeBytes}>
                          {formatBytes(tableBytes)}
                        </ThemedText>
                        <ThemedText style={[styles.tablePercentText, { color: textMuted }]}>
                          ({percentOfTotal.toFixed(1)}%)
                        </ThemedText>
                      </View>
                    </View>

                    <ThemedProgressBar
                      progress={percentOfTotal / 100}
                      color="#0284C7"
                      style={styles.tableProgressBar}
                    />

                    <View style={styles.tableMetaRow}>
                      <ThemedText style={[styles.tableMetaText, { color: textMuted }]}>
                        📄 {formatCount(table.docCount)} docs
                      </ThemedText>
                      <ThemedText style={[styles.tableMetaText, { color: textMuted }]}>
                        ⚡ Avg doc: {formatBytes(table.avgDocSizeBytes)}
                      </ThemedText>
                    </View>
                  </View>
                );
              })
            )}
          </View>
        </ThemedPanel>
      </ScrollView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  unauthorizedContainer: {
    padding: Spacing.four,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 64,
  },
  scrollContent: {
    padding: Spacing.three,
    paddingBottom: 64,
  },
  statusBarPanel: {
    marginBottom: Spacing.three,
    padding: Spacing.three,
  },
  statusInfoRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: Spacing.two,
  },
  statusBadgeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  statusIndicatorDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  statusBadgeText: {
    fontSize: 13,
  },
  statusTimestamp: {
    fontSize: 12,
  },
  actionButtonsRow: {
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
    flexWrap: "wrap",
  },
  actionBtn: {
    minWidth: 120,
  },
  loadingBanner: {
    flexDirection: "row",
    alignItems: "center",
    padding: Spacing.three,
    marginBottom: Spacing.three,
  },
  sectionPanel: {
    marginBottom: Spacing.three,
    padding: Spacing.three,
  },
  sectionHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginBottom: Spacing.three,
  },
  sectionIconContainer: {
    width: 32,
    height: 32,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  sectionTitle: {
    fontSize: 16,
  },
  metricsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
  },
  metricCard: {
    flexGrow: 1,
    flexBasis: 160,
    borderWidth: 1,
    borderRadius: 8,
    padding: Spacing.three,
    minHeight: 90,
    justifyContent: "space-between",
  },
  metricLabel: {
    fontSize: 12,
    fontWeight: "500",
  },
  metricValue: {
    fontSize: 20,
    marginVertical: 4,
  },
  metricDescription: {
    fontSize: 11,
    lineHeight: 14,
  },
  dbTotalsBanner: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-around",
    borderWidth: 1,
    borderRadius: 8,
    padding: Spacing.two,
    marginBottom: Spacing.three,
    flexWrap: "wrap",
    gap: 8,
  },
  dbTotalItem: {
    alignItems: "center",
    paddingHorizontal: 8,
  },
  dbTotalLabel: {
    fontSize: 11,
    marginBottom: 2,
  },
  dbDivider: {
    width: 1,
    height: 24,
  },
  tableListContainer: {
    gap: 8,
  },
  tableRowCard: {
    borderWidth: 1,
    borderRadius: 8,
    padding: Spacing.two + 2,
    gap: 6,
  },
  tableRowTop: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  tableNameBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  tableNameText: {
    fontSize: 14,
  },
  tableSizeMetrics: {
    flexDirection: "row",
    alignItems: "baseline",
    gap: 4,
  },
  tableSizeBytes: {
    fontSize: 14,
  },
  tablePercentText: {
    fontSize: 11,
  },
  tableProgressBar: {
    height: 4,
    borderRadius: 2,
  },
  tableMetaRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  tableMetaText: {
    fontSize: 11,
  },
  cloudMonitoringPanel: {
    marginBottom: Spacing.three,
    padding: Spacing.three,
  },
  cloudMonitoringHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: Spacing.two,
  },
  cloudMonitoringIcon: {
    width: 38,
    height: 38,
    borderRadius: 8,
    backgroundColor: "rgba(2, 132, 199, 0.12)",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  cloudMonitoringTextContainer: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  cloudMonitoringTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
  },
  cloudMonitoringTitle: {
    fontSize: 14,
  },
  projectPill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: "rgba(2, 132, 199, 0.12)",
  },
  projectPillText: {
    fontSize: 11,
    fontWeight: "600",
    color: "#0284C7",
  },
  cloudMonitoringDesc: {
    fontSize: 12,
    lineHeight: 17,
  },
  cloudMonitoringLinksRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 4,
  },
  cloudMonitoringLinkBtn: {
    flex: 1,
    minWidth: 220,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
    borderRadius: 6,
    borderWidth: 1,
  },
  cloudMonitoringLinkText: {
    fontSize: 12,
    fontWeight: "600",
  },
});
